import type { ReactNode } from 'react';

import { Button, type ButtonProps } from '../Button';
import { Flex } from '../Flex';
import { useStyles } from './ActionButtons.styles';

export interface ActionButtonConfig extends ButtonProps {
  displayLabel: string;
  accessibleLabel: string;
  id?: string;
}

interface ActionButtonsProps {
  buttons: ActionButtonConfig[];
  /**
   * Optional custom content to display on the left side of the action buttons.
   * The buttons are right-aligned, so this content appears in the left section.
   * Useful for displaying labels, counts, or other non-button content.
   *
   * @example
   * ```tsx
   * customActionsContent={
   *   <Typography variant="body2" color="text.secondary">
   *     5 of 10 items selected
   *   </Typography>
   * }
   * ```
   */
  customActionsContent?: ReactNode;
}

const ActionButtons = ({
  buttons,
  customActionsContent,
}: ActionButtonsProps): JSX.Element => {
  const { classes } = useStyles();

  // If there aren't any buttons, assume the wrapper should take the full width.
  // there's currently only one instance of this component that doesn't have any buttons, but has custom actions content (FailedVehiclesModal)
  const isButtonConfigEmpty = buttons.length === 0;
  const customActionsContainer = customActionsContent ? (
    <Flex
      center={!isButtonConfigEmpty}
      className={isButtonConfigEmpty ? classes.customActionsContentWrapper : ''}
      dataTestId="action-buttons-custom-content"
    >
      {customActionsContent}
    </Flex>
  ) : null;

  const hasBothDestructiveAndPrimary =
    buttons.some((btn) => btn.destructive) &&
    buttons.some(
      (btn) => !btn.destructive && (!btn.variant || btn.variant === 'primary')
    );

  if (!hasBothDestructiveAndPrimary) {
    // If not both types present, render all buttons right-aligned
    return (
      <Flex
        className={`${classes.buttonContainer} ${customActionsContent ? 'has-custom-content' : ''}`}
        dataTestId="action-buttons-container"
      >
        {customActionsContainer}
        {buttons.map((config, index) => (
          <Button
            size="large"
            startIcon={config.startIcon}
            endIcon={config.endIcon}
            key={config.id || index}
            aria-label={config.accessibleLabel}
            variant={config.variant || 'primary'}
            onClick={config.onClick}
            isLoading={config.isLoading}
            disabled={config.disabled}
            id={config.id}
            data-testid={config.id}
            heapId={config.heapId}
            destructive={config.destructive}
          >
            {config.displayLabel}
          </Button>
        ))}
      </Flex>
    );
  }

  // When both destructive and primary buttons are present
  const destructiveButtons = buttons.filter((btn) => btn.destructive);
  const otherButtons = buttons.filter((btn) => !btn.destructive);

  return (
    <Flex
      className={`${classes.splitButtonContainer} ${customActionsContent ? 'has-custom-content' : ''}`}
      dataTestId="action-buttons-split-container"
    >
      {/* Render left-side buttons */}
      <Flex
        className={classes.buttonGroup}
        dataTestId="action-buttons-left-group"
      >
        {destructiveButtons.map((config, index) => (
          <Button
            size="large"
            startIcon={config.startIcon}
            endIcon={config.endIcon}
            key={config.id || `destructive-${index}`}
            aria-label={config.accessibleLabel}
            variant={config.variant || 'primary'}
            onClick={config.onClick}
            isLoading={config.isLoading}
            disabled={config.disabled}
            id={config.id}
            data-testid={config.id}
            heapId={config.heapId}
            destructive={config.destructive}
          >
            {config.displayLabel}
          </Button>
        ))}
      </Flex>

      {/* Render right-side buttons */}
      <Flex
        className={classes.rightButtonGroup}
        dataTestId="action-buttons-right-group"
      >
        {customActionsContainer}
        {otherButtons.map((config, index) => (
          <Button
            size="large"
            startIcon={config.startIcon}
            endIcon={config.endIcon}
            key={config.id || `other-${index}`}
            aria-label={config.accessibleLabel}
            variant={config.variant || 'primary'}
            onClick={config.onClick}
            isLoading={config.isLoading}
            disabled={config.disabled}
            id={config.id}
            data-testid={config.id}
            heapId={config.heapId}
            destructive={config.destructive}
          >
            {config.displayLabel}
          </Button>
        ))}
      </Flex>
    </Flex>
  );
};

export default ActionButtons;
